/**
 * Yalıda Davet · shared business rules
 * SINGLE SOURCE OF TRUTH for the matching engine, consumed by both the
 * Cloudflare Worker (apps/api) and the React client (apps/web).
 * Mirrors the `event_tiers` rows seeded in db/seed/0001_seed.sql.
 */

export const PLATFORM_MAX_GUESTS = 150;
export const HARD_CHECKOUT_DEADLINE = '23:30'; // 11:30 PM neighbourhood hard stop
export const DEFAULT_NOISE_CURFEW = '23:00';

export type Tier = 1 | 2 | 3;

export interface EventTier {
  tier: Tier;
  labelTr: string;
  minGuests: number;
  maxGuests: number;
  /** Hard minimum garden size in m² for this guest band. */
  minGardenM2: number;
  /** Upper band used for UI copy, e.g. "250 - 300 m²". */
  recommendedGardenM2: number;
  /** Damage deposit = round(nightlyBase * multiplier) + flat. */
  depositMultiplier: number;
  depositFlatKurus: number;
}

export const EVENT_TIERS: readonly EventTier[] = [
  { tier: 1, labelTr: 'Küçük Organizasyon (50 kişi altı)', minGuests: 1, maxGuests: 49, minGardenM2: 150, recommendedGardenM2: 150, depositMultiplier: 0.2, depositFlatKurus: 500_000 },
  { tier: 2, labelTr: 'Orta Ölçekli (50-100 kişi)', minGuests: 50, maxGuests: 100, minGardenM2: 250, recommendedGardenM2: 300, depositMultiplier: 0.35, depositFlatKurus: 1_000_000 },
  { tier: 3, labelTr: 'Büyük Organizasyon (100-150 kişi)', minGuests: 101, maxGuests: 150, minGardenM2: 300, recommendedGardenM2: 350, depositMultiplier: 0.5, depositFlatKurus: 2_000_000 },
] as const;

export function getTierForGuests(guestCount: number): EventTier {
  if (!Number.isInteger(guestCount) || guestCount < 1) {
    throw new Error('GUEST_COUNT_INVALID');
  }
  const tier = EVENT_TIERS.find((t) => guestCount >= t.minGuests && guestCount <= t.maxGuests);
  if (!tier) throw new Error('GUEST_COUNT_EXCEEDS_PLATFORM_LIMIT');
  return tier;
}

export function requiredGardenM2(guestCount: number): number {
  return getTierForGuests(guestCount).minGardenM2;
}

export type EligibilityReason =
  | 'OK'
  | 'CAPACITY_EXCEEDED'
  | 'GARDEN_TOO_SMALL'
  | 'GUEST_COUNT_EXCEEDS_PLATFORM_LIMIT'
  | 'EVENT_TYPE_NOT_APPROVED'
  | 'MISSING_STAGING_AREA'
  | 'MISSING_CATERING_AREA';

export interface EligibilityInput {
  guestCount: number;
  gardenSizeM2: number;
  maxCapacity: number;
  eventTypeId?: string;
  approvedEventTypeIds?: string[];
  hasStagingArea?: boolean;
  hasCateringArea?: boolean;
}

export interface EligibilityResult {
  eligible: boolean;
  reason: EligibilityReason;
  tier: Tier | null;
  requiredGardenM2: number | null;
  messageTr: string;
}

/** Event types that force staging/catering availability flags. */
export const STAGING_REQUIRED_EVENTS = new Set(['dugun', 'nikah', 'nisan']);
export const CATERING_REQUIRED_EVENTS = new Set(['dugun']);

export function checkEligibility(input: EligibilityInput): EligibilityResult {
  const { guestCount, gardenSizeM2, maxCapacity, eventTypeId } = input;

  if (guestCount > PLATFORM_MAX_GUESTS) {
    return { eligible: false, reason: 'GUEST_COUNT_EXCEEDS_PLATFORM_LIMIT', tier: null, requiredGardenM2: null, messageTr: `Platform limiti ${PLATFORM_MAX_GUESTS} misafirdir.` };
  }

  // HARD STOP — checked before anything else so the UI can block submission.
  if (guestCount > maxCapacity) {
    return { eligible: false, reason: 'CAPACITY_EXCEEDED', tier: null, requiredGardenM2: null, messageTr: `Bu mülkün maksimum kapasitesi ${maxCapacity} kişidir.` };
  }

  const tier = getTierForGuests(guestCount);
  if (gardenSizeM2 < tier.minGardenM2) {
    return { eligible: false, reason: 'GARDEN_TOO_SMALL', tier: tier.tier, requiredGardenM2: tier.minGardenM2, messageTr: `${guestCount} misafir için en az ${tier.minGardenM2} m² bahçe gereklidir (bu mülk: ${gardenSizeM2} m²).` };
  }

  if (eventTypeId) {
    if (input.approvedEventTypeIds && !input.approvedEventTypeIds.includes(eventTypeId)) {
      return { eligible: false, reason: 'EVENT_TYPE_NOT_APPROVED', tier: tier.tier, requiredGardenM2: tier.minGardenM2, messageTr: 'Bu mülk seçilen organizasyon tipi için onaylı değildir.' };
    }
    if (STAGING_REQUIRED_EVENTS.has(eventTypeId) && input.hasStagingArea === false) {
      return { eligible: false, reason: 'MISSING_STAGING_AREA', tier: tier.tier, requiredGardenM2: tier.minGardenM2, messageTr: 'Bu organizasyon tipi için sahne/kurulum alanı zorunludur.' };
    }
    if (CATERING_REQUIRED_EVENTS.has(eventTypeId) && input.hasCateringArea === false) {
      return { eligible: false, reason: 'MISSING_CATERING_AREA', tier: tier.tier, requiredGardenM2: tier.minGardenM2, messageTr: 'Düğün için catering/servis alanı zorunludur.' };
    }
  }

  return { eligible: true, reason: 'OK', tier: tier.tier, requiredGardenM2: tier.minGardenM2, messageTr: 'Uygun' };
}

/** Damage deposit in kuruş, scaling with the guest-count tier. */
export function calculateDepositKurus(nightlyBaseKurus: number, guestCount: number): number {
  const tier = getTierForGuests(guestCount);
  return Math.round(nightlyBaseKurus * tier.depositMultiplier) + tier.depositFlatKurus;
}
